// WSOLA (Waveform Similarity Overlap-Add) time-stretcher: changes audio playback speed while
// preserving pitch. AudioBufferSourceNode.playbackRate (used for the non-pitch-preserving path)
// is a straight resample - it shifts pitch proportionally to the rate, unlike HTMLMediaElement's
// own automatic pitch correction that the live preview relies on for its <video> elements. This
// closes that gap for the export's offline audio mix.
//
// Pure-data core (Float32Array in, Float32Array out) so the DSP itself is unit-testable without
// a real (Offline)AudioContext; see timeStretchAudioBuffer below for the AudioBuffer-level wrapper
// actually used by audioMixer.js.
const FRAME_MS = 40
const SEARCH_MS = 15

export const timeStretchChannelData = (input, rate, sampleRate) => {
    const inputLength = input.length
    const outputLength = Math.max(1, Math.round(inputLength / rate))

    const synthesisHop = Math.max(1, Math.round((sampleRate * FRAME_MS) / 1000 / 2))
    const frameSize = synthesisHop * 2
    const analysisHop = Math.max(1, Math.round(synthesisHop * rate))
    const searchRadius = Math.round((sampleRate * SEARCH_MS) / 1000)

    // Too short to build even one full analysis frame - a plain resample still changes pitch, but
    // it's a graceful fallback instead of the windowing math below going out of bounds.
    if (inputLength < frameSize * 2) {
        const output = new Float32Array(outputLength)
        for (let i = 0; i < outputLength; i++) {
            const srcIdx = Math.min(inputLength - 1, Math.round(i * rate))
            output[i] = input[srcIdx]
        }
        return output
    }

    const window = new Float32Array(frameSize)
    for (let i = 0; i < frameSize; i++) {
        window[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (frameSize - 1))
    }

    const output = new Float32Array(outputLength)
    const weight = new Float32Array(outputLength)

    let prevFrameTail = null
    let synthesisPos = 0
    let idealAnalysisPos = 0

    while (synthesisPos < outputLength) {
        let readPos = Math.round(idealAnalysisPos)

        // Search a small window around the ideal (drifting) read position for the offset whose
        // frame best continues the previous one's waveform - without this, a naive fixed-hop
        // overlap-add produces audible clicks/phasiness at every frame boundary.
        if (prevFrameTail) {
            let bestOffset = 0
            let bestScore = -Infinity
            for (let off = -searchRadius; off <= searchRadius; off++) {
                const candidatePos = readPos + off
                if (candidatePos < 0 || candidatePos + frameSize > inputLength) continue
                let score = 0
                for (let i = 0; i < synthesisHop; i++) {
                    score += input[candidatePos + i] * prevFrameTail[i]
                }
                if (score > bestScore) { bestScore = score; bestOffset = off }
            }
            readPos += bestOffset
        }

        readPos = Math.max(0, Math.min(readPos, inputLength - frameSize))

        const frame = new Float32Array(frameSize)
        for (let i = 0; i < frameSize; i++) {
            frame[i] = input[readPos + i] * window[i]
        }

        for (let i = 0; i < frameSize; i++) {
            const outIdx = synthesisPos + i
            if (outIdx >= 0 && outIdx < outputLength) {
                output[outIdx] += frame[i]
                weight[outIdx] += window[i]
            }
        }

        prevFrameTail = frame.subarray(synthesisHop)
        synthesisPos += synthesisHop
        idealAnalysisPos += analysisHop
    }

    // Hann windows at exactly 50% overlap sum to a constant on their own, but the WSOLA search
    // above shifts frames off that exact grid, so overlap coverage isn't perfectly uniform -
    // normalizing by the accumulated window weight avoids amplitude bumps/dips from that drift.
    for (let i = 0; i < outputLength; i++) {
        output[i] = weight[i] > 0.0001 ? output[i] / weight[i] : 0
    }

    return output
}

export const timeStretchAudioBuffer = (ctx, inputBuffer, rate) => {
    if (!rate || rate === 1) return inputBuffer

    const outputLength = Math.max(1, Math.round(inputBuffer.length / rate))
    const outputBuffer = ctx.createBuffer(inputBuffer.numberOfChannels, outputLength, inputBuffer.sampleRate)

    for (let ch = 0; ch < inputBuffer.numberOfChannels; ch++) {
        const stretched = timeStretchChannelData(inputBuffer.getChannelData(ch), rate, inputBuffer.sampleRate)
        outputBuffer.copyToChannel(stretched, ch)
    }

    return outputBuffer
}
